import { isSystemTag } from '@tunasong/models'

/**
 * The list bar's text filter (GH #2519 A; Joni's search & filter polish spec, A). It narrows one list and
 * never searches the library, which is Search's job.
 *
 * - Plain text matches the name anywhere, or a tag at the start of any of its words: `prog` finds
 *   "Progression" and the tag `in-progress`, but `gress` finds no tag.
 * - A leading `#` matches tags only: `#live`.
 * - System tags (`sys:*`) never match: they are hidden by design.
 * - Case- and accent-insensitive.
 */

interface Filterable {
  name?: string
  tags?: string[]
}

/** Lower case, accents stripped, so "Café" and "cafe" are the same */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

export const matchesListText = (item: Filterable, text: string): boolean => {
  const query = fold(text.trim())
  const isTagOnly = query.startsWith('#')
  const needle = isTagOnly ? query.slice(1) : query
  if (needle === '') {
    return true
  }
  const tags = (item.tags ?? []).filter(tag => !isSystemTag(tag)).map(fold)
  /** A tag's words start at its start and after each separator: in-progress is "in" and "progress" */
  const startsAWord = (tag: string) =>
    tag.startsWith(needle) || tag.split(/[^\p{L}\p{N}]+/u).some(word => word.startsWith(needle))
  if (tags.some(startsAWord)) {
    return true
  }
  return !isTagOnly && fold(item.name ?? '').includes(needle)
}

/** The items matching `text`, in their order; the list itself when there is no text */
export const filterByText = <T extends Filterable>(items: T[], text: string): T[] =>
  text.trim() === '' ? items : items.filter(item => matchesListText(item, text))
